import * as React from 'react';
import Button from 'react-bootstrap/Button';
import { FormattedMessage } from 'react-intl';
import {
  GlobalModalActionsEnum,
  ModalActionDescription,
  ModalActionsEnum,
} from '../../../../document-table/DocumentTableConstants';
import PropdocsModal from '../../../../modal/PropdocsModal';
import { DocumentLoadingSpinner } from '../../../document-loading-spinner/DocumentLoadingSpinner';
import LoadingSpinner from '../../../../loading-spinner';
import InviteReceivingTeamFormik from '../../../../../forms/InviteReceivingTeamFormContainer';
import { confirmButtonTitle } from '../../../document-editor/utils/defineConfirmButtonTitle';
import { Warning } from '../../../../shared/warning';
import { OptionalMessageInputRef } from './OptionalMessageInputRef';

interface DocumentWizardActionModalProps {
  show: boolean;
  onHide: () => void;
  handler?: () => void;
  isDealShared?: boolean;
  isLoading?: boolean;
  isPossessionPending?: boolean;
  title?: string;
  activeModal?: GlobalModalActionsEnum | null;
  children?: React.ReactNode;
  suggestionsAlert?: {
    text: string;
    title: string;
  } | null;
  optionalMessageInputRef?: any;
  teamId: string;
  showCounterparty?: boolean;
  counterpartyName?: string;
  counterpartyEmail?: string;
  hasCounterparty?: boolean;
}

export function DocumentWizardActionModal({ handler = () => {}, ...props }: DocumentWizardActionModalProps) {
  const isActiveSendDraftModal = props.activeModal === ModalActionsEnum.SEND;
  const isDealShared = props.isDealShared;
  const isActiveSendForSigningModal = props.activeModal === ModalActionsEnum.SEND_FOR_SIGNING;
  const isActiveTerminateModal = props.activeModal === ModalActionsEnum.TERMINATE;
  const isGrantAccessModalActive = props.activeModal === ModalActionsEnum.GRANT_ACCESS;

  const showCounterparty = props.showCounterparty || false;
  const validCounterparty = showCounterparty && props.counterpartyEmail && props.counterpartyName;
  const hasCounterparty = props.hasCounterparty || false;

  const BodyContainer = React.memo(({ children }: { children?: React.ReactNode }) => (
    <div style={{ marginBottom: '1rem' }}>{children}</div>
  ));

  const ActionsContainer = React.memo(({ children }: { children?: React.ReactNode }) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>{children}</div>
  ));

  return (
    <PropdocsModal
      size="lg"
      show={props.show}
      setShow={props.onHide}
      title={
        <header>
          {!(isActiveSendDraftModal || isActiveSendForSigningModal) ? (
            <FormattedMessage id="document-wizard-modal.lease-terminate-title" defaultMessage={props.title} />
          ) : (
            props.title
          )}
        </header>
      }
    >
      <>
        {isActiveSendDraftModal && validCounterparty && (
          <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <strong>Counterparty:</strong>&nbsp;{props.counterpartyName} ({props.counterpartyEmail})
          </div>
        )}
        {props.suggestionsAlert && (
          <div style={{ marginBottom: '1rem' }}>
            <Warning text={props.suggestionsAlert.text} title={props.suggestionsAlert.title} />
          </div>
        )}

        {(isActiveSendDraftModal || isActiveSendForSigningModal) && isDealShared && (
          <div style={{ marginBottom: '1.5rem' }}>
            <FormattedMessage
              id="lease-document-wizard-preview-send-offer.description"
              defaultMessage={
                isActiveSendForSigningModal ? ModalActionDescription.SEND_FOR_SIGNING : ModalActionDescription.SEND
              }
            />
          </div>
        )}

        {isGrantAccessModalActive && isDealShared && (
          <div style={{ marginBottom: '1rem' }}>
            <FormattedMessage
              id="lease-document-wizard-preview-send-offer.description"
              defaultMessage={ModalActionDescription.GRANT_ACCESS}
            />
          </div>
        )}

        {!isGrantAccessModalActive && props.children && <div style={{ marginBottom: '1rem' }}>{props.children}</div>}

        {(isActiveSendDraftModal || isActiveSendForSigningModal || isGrantAccessModalActive) &&
        !isDealShared &&
        !hasCounterparty ? (
          <InviteReceivingTeamFormik
            shouldAccessBeGranted={isGrantAccessModalActive}
            sendDocumentToReceiverHandler={handler}
            renderSecondaryAction={() => (
              <Button variant="link" className="text-secondary" onClick={props.onHide}>
                <FormattedMessage id="lease-document-wizard-preview-send-offer.cta.cancel" defaultMessage="Cancel" />
              </Button>
            )}
            bodyContainer={BodyContainer}
            actionsContainer={ActionsContainer}
            teamId={props.teamId}
            sendWithDocumentId={true}
            shareDocument={true}
            refreshCounterpartyData={() => {}}
          />
        ) : (
          <>
            <OptionalMessageInputRef ref={props.optionalMessageInputRef} />
            {(isDealShared || isActiveTerminateModal || hasCounterparty) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
                <Button variant="link" className="text-secondary" onClick={props.onHide}>
                  <FormattedMessage id="lease-document-wizard-preview-send-offer.cta.cancel" defaultMessage="Cancel" />
                </Button>
                <Button variant="secondary" onClick={handler} disabled={props.isPossessionPending || props.isLoading}>
                  <FormattedMessage
                    id="lease-document-wizard-preview-send-offer.cta.send-to-tenant"
                    defaultMessage={confirmButtonTitle(props.activeModal)}
                  />
                  {props.isPossessionPending && <DocumentLoadingSpinner />}
                  {props.isLoading && <LoadingSpinner />}
                </Button>
              </div>
            )}
          </>
        )}
      </>
    </PropdocsModal>
  );
}
